import type { Capability } from "@enduring-systems/entitlements";

import { Badge } from "@/common/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";

import { formatEntitlementValue, grantTierLabel } from "../effective";
import type {
  EffectiveAtScope,
  EngineRowState,
  ScopeStateCapability,
  ScopeStateView,
  StoredGrantView,
} from "../entitlements-api";
import { capabilityNamespace } from "./offering-state";

type GrantTier = "base" | "override";

interface StoredRow {
  readonly tier: GrantTier;
  readonly row: StoredGrantView;
}

/**
 * Both stored tiers, override first, because BOTH matter here.
 *
 * Not "the row that wins": `evaluate()` checks every grant in the chain rather
 * than only the winner, so a base row the registry type refuses denies the
 * capability even underneath an honored override. A table that showed only the
 * winning row would hide the row the operator has to fix.
 */
function storedRows(entry: ScopeStateCapability): readonly StoredRow[] {
  const rows: StoredRow[] = [];
  if (entry.override !== null) rows.push({ tier: "override", row: entry.override });
  if (entry.base !== null) rows.push({ tier: "base", row: entry.base });
  return rows;
}

function effectiveText(effective: EffectiveAtScope): string {
  return effective.inForce
    ? formatEntitlementValue(effective.type, effective.value)
    : effective.reason;
}

function engineLabel(state: Exclude<EngineRowState, "honored">): string {
  return state === "ignored" ? "ignored" : "denying";
}

export interface ScopeStateTableProps {
  readonly view: ScopeStateView;
  readonly capabilities: readonly Capability[];
}

/**
 * Everything stored at one scope, as the engine reads it (ENR-1939).
 *
 * `effective` is the read's own verdict, grounded on `evaluate()`, and is never
 * recomputed here: an operator comparing this table against the offering cards
 * has to be comparing one decision shown twice rather than two decisions that
 * can disagree.
 *
 * Scope-local by design. A capability showing nothing in force here may still
 * resolve from an ancestor, which this read deliberately does not walk, so the
 * empty state says so instead of implying the capability is off.
 */
export function ScopeStateTable({ view, capabilities }: ScopeStateTableProps): React.ReactElement {
  if (view.capabilities.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground" data-testid="scope-state-empty">
          Nothing is set at this scope. Values still resolve from ancestors and from capability
          defaults, which this scope-local read does not walk.
        </p>
        <UnreadableNote count={view.unreadable} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Its own scroller: a five-column table must not push the page sideways
          on a narrow viewport. */}
      <div className="overflow-x-auto" data-testid="scope-state-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Capability</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Granted</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.capabilities.map((entry) => {
              const rows = storedRows(entry);
              return (
                <TableRow
                  key={entry.capabilityId}
                  data-testid={`scope-state-row-${entry.capabilityId}`}
                >
                  <TableCell className="font-mono text-xs">{entry.capabilityId}</TableCell>
                  <TableCell>
                    {capabilityNamespace(entry.capabilityId, capabilities) ?? "unknown"}
                  </TableCell>
                  <TableCell>
                    {rows.length === 0
                      ? "nothing"
                      : rows.map(({ tier, row }) => (
                          <span key={tier} className="block">
                            {grantTierLabel(tier)}: {formatEntitlementValue(row.type, row.value)}
                          </span>
                        ))}
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {effectiveText(entry.effective)}
                      {rows.map(({ tier, row }) =>
                        row.engineState === "honored" ? null : (
                          <Badge
                            key={tier}
                            variant="destructive"
                            data-testid={`scope-state-engine-${entry.capabilityId}-${tier}`}
                          >
                            {grantTierLabel(tier)} {engineLabel(row.engineState)}
                          </Badge>
                        )
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {rows.length === 0
                      ? "no grant at this scope"
                      : rows.map(({ tier, row }) => (
                          <span key={tier} className="block">
                            {row.provenance ?? "no recorded source"}
                          </span>
                        ))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <UnreadableNote count={view.unreadable} />
    </div>
  );
}

function UnreadableNote({ count }: { readonly count: number }): React.ReactElement | null {
  if (count === 0) return null;
  return (
    <p className="text-sm text-destructive" data-testid="scope-state-unreadable">
      {count} stored {count === 1 ? "row" : "rows"} could not be read, so this table is incomplete.
    </p>
  );
}
